import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spin, message, Modal } from 'antd';
import { PlusOutlined, FileTextOutlined, FilePdfOutlined } from '@ant-design/icons';
import html2pdf from 'html2pdf.js';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { formatMoney } from '../../utils/currency';
import FormikForm from '../form/FormikForm';
import './Borsa.css';

const BORSA_WS_URL = process.env.REACT_APP_BORSA_WS_URL || 'wss://atm.iyi.im:8765';

function formatTRY(value) {
    return formatMoney(value);
}

function formatUSD(value) {
    return formatMoney(value, 'USD');
}

const Borsa = ({
    // Hisse formunun mysql_id'si. Uc `form_id: int` bekliyor; buraya string
    // verilirse /form/formlar/{id}/schema 422 doner ve modal "Form Yukleme
    // Hatasi" gosterir. Ekleme tarafi (handleAdd) zaten 306 kullaniyordu.
    formIdHisse = 306,
    tableNameHisse = 'borsa',
}) => {
    const navigate = useNavigate();
    const { darkMode } = useTheme();
    const [loading, setLoading] = useState(true);
    const [hisseler, setHisseler] = useState([]);
    const [guncelDolar, setGuncelDolar] = useState(1);
    const [bist100, setBist100] = useState('');
    const [bist100Flash, setBist100Flash] = useState(null);
    const [rowColors, setRowColors] = useState({});
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState(null);
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState('');
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [currentFormExtraData, setCurrentFormExtraData] = useState({});
    const wsRef = useRef(null);
    const tradingViewRef = useRef(null);
    const tableRef = useRef(null);

    const fetchHisseler = useCallback(async (doRefresh = false) => {
        try {
            setLoading(true);
            if (doRefresh) {
                try {
                    await fetch(`${API_BASE_URL}/borsa/refresh`, { method: 'POST' });
                    message.success('Güncel borsa verisi yüklendi!');
                } catch (e) {
                    console.warn('Borsa refresh:', e);
                }
            }
            const res = await fetch(`${API_BASE_URL}/borsa/hisseler`);
            if (!res.ok) throw new Error(res.statusText);
            const data = await res.json();
            setHisseler(data.hisseler || []);
            setGuncelDolar(Number(data.guncel_dolar) || 1);
            setBist100(data.bist100 != null ? String(data.bist100) : '');
            setRowColors({});
        } catch (e) {
            message.error('Borsa verisi yüklenemedi: ' + (e.message || e));
            setHisseler([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHisseler(true);
    }, [fetchHisseler]);

    useEffect(() => {
        if (bist100Flash) {
            const t = setTimeout(() => setBist100Flash(null), 1500);
            return () => clearTimeout(t);
        }
    }, [bist100Flash]);

    useEffect(() => {
        const ws = new WebSocket(BORSA_WS_URL);
        wsRef.current = ws;

        ws.addEventListener('open', () => {
            fetchHisseler(false);
        });

        ws.addEventListener('message', (event) => {
            const msg = JSON.parse(event.data);
            const content = msg.Content || msg.content || msg;
            const symbol = content.Symbol || content.symbol;
            const price = parseFloat(content.Price ?? content.price ?? 0);
            const change = parseFloat(content.Change ?? content.change ?? 0);
            if (symbol == null || Number.isNaN(price)) return;

            const color = change > 0 ? 'greenflash' : change < 0 ? 'redflash' : '';

            if (symbol === 'USDTRY') {
                setGuncelDolar(price);
                setHisseler((prev) =>
                    prev.map((h) => ({ ...h, guncel_dolar: price }))
                );
                return;
            }
            if (symbol === 'XU100') {
                setBist100(String(price));
                setBist100Flash(change > 0 ? 'greenflash' : change < 0 ? 'redflash' : null);
                return;
            }

            setRowColors((c) => ({ ...c, [symbol]: color }));
            setHisseler((prev) =>
                prev.map((h) =>
                    h.kod === symbol ? { ...h, guncel_fiyat: price } : h
                )
            );
        });

        ws.addEventListener('close', () => { });
        ws.addEventListener('error', () => { });

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [fetchHisseler]);

    const watchlist = useMemo(
        () =>
            [...new Set(hisseler.filter((h) => h.kod && h.kurum !== 'TOPLAM').map((h) => h.kod))].map(
                (kod) => `BIST:${kod}`
            ),
        [hisseler]
    );
    const firstSymbol = watchlist[0] || 'BIST:XU100';
    const watchlistKey = watchlist.join(',');

    useEffect(() => {
        if (watchlist.length === 0) return;
        const container = document.getElementById('tradingview_borsa');
        if (!container) return;
        const initTradingView = () => {
            if (typeof window.TradingView === 'undefined') return;
            container.innerHTML = '';
            try {
                tradingViewRef.current = new window.TradingView.widget({
                    autosize: true,
                    symbol: firstSymbol,
                    interval: 'D',
                    timezone: 'Etc/UTC',
                    theme: 'light',
                    style: '1',
                    locale: 'tr',
                    enable_publishing: false,
                    withdateranges: true,
                    hide_side_toolbar: false,
                    allow_symbol_change: true,
                    watchlist,
                    details: true,
                    hotlist: true,
                    calendar: true,
                    container_id: 'tradingview_borsa',
                });
            } catch (e) {
                console.warn('TradingView widget:', e);
            }
        };
        if (window.TradingView) {
            initTradingView();
        } else {
            const script = document.createElement('script');
            script.src = 'https://s3.tradingview.com/tv.js';
            script.async = true;
            script.onload = initTradingView;
            document.body.appendChild(script);
        }
        return () => {
            if (container) container.innerHTML = '';
            tradingViewRef.current = null;
        };
    }, [firstSymbol, watchlistKey]);

    const handleOpenForm = (formId, title = 'Hisse Senedi', tableName = '', uniqueField = '', uniqueValue = null, extraDataOrWrapper = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        const extra = extraDataOrWrapper?.extraData != null ? extraDataOrWrapper.extraData : (extraDataOrWrapper || {});
        setCurrentFormExtraData(extra);
        setFormModalOpen(true);
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        setCurrentUniqueValue(null);
        setCurrentFormExtraData({});
        fetchHisseler(false);
    };

    const handleAdd = () => {
        handleOpenForm(306, 'Hisse Senedi', 'borsa', 'id', null, {});
    };

    const handleEdit = (id) => {
        // Form 306'nin tanimli function'i Insert; override verilmezse duzenleme
        // mevcut satiri guncellemek yerine kopya kayit ekler.
        handleOpenForm(formIdHisse, 'Düzenle', tableNameHisse, 'id', id, { _override_function: 'Update' });
    };

    const goGunluk = () => {
        navigate('/borsa_gunluk');
    };

    const handlePdfDownload = async () => {
        const el = tableRef.current;
        if (!el) {
            message.error('Tablo bulunamadı.');
            return;
        }
        try {
            message.loading({ content: 'PDF oluşturuluyor...', key: 'pdf' });
    
            await html2pdf()
                .set({
                    margin: 5,
                    filename: `Borsa_Raporu_${new Date().toISOString().slice(0, 10)}.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { 
                        scale: 2, 
                        backgroundColor: '#ffffff',
                        windowWidth: 1600, 
                        onclone: (clonedDoc) => {
                            const clonedWrap = clonedDoc.querySelector('.borsa-table-wrap');
                            if (clonedWrap) {
                                clonedWrap.style.width = '1500px';
                                clonedWrap.style.maxWidth = 'none';
                                clonedWrap.style.overflow = 'visible'; 
                            }
    
                            const cells = clonedDoc.querySelectorAll('.borsatable th, .borsatable td');
                            cells.forEach(cell => {
                                cell.style.fontSize = '10px';
                                cell.style.padding = '4px';
                                cell.style.whiteSpace = 'nowrap'; 
                            });
                        }
                    },
                    jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
                })
                .from(el)
                .save();
    
            message.success({ content: 'PDF indirildi.', key: 'pdf' });
        } catch (e) {
            message.error({ content: 'PDF oluşturulamadı: ' + (e.message || e), key: 'pdf' });
        }
    };

    const toplamFilter = (h) => h.kurum !== 'TOPLAM';

    const toplamOrtMaliyetTRY = hisseler
        .filter(toplamFilter)
        .reduce((acc, h) => acc + (Number(h.ort_maliyet) || 0) * (Number(h.adet) || 0), 0);
    const toplamGuncelTutarTRY = hisseler
        .filter(toplamFilter)
        .reduce((acc, h) => acc + (Number(h.guncel_fiyat) || 0) * (Number(h.adet) || 0), 0);
    const toplamKarZararTRY = toplamGuncelTutarTRY - toplamOrtMaliyetTRY;
    const toplamOrtMaliyetUSD = hisseler
        .filter(toplamFilter)
        .reduce((acc, h) => acc + ((Number(h.ort_maliyet) || 0) / (Number(h.dolar) || 1)) * (Number(h.adet) || 0), 0);
    const toplamGuncelTutarUSD = hisseler
        .filter(toplamFilter)
        .reduce((acc, h) => acc + ((Number(h.guncel_fiyat) || 0) / (Number(h.guncel_dolar) || 1)) * (Number(h.adet) || 0), 0);
    const toplamKarZararUSD = toplamGuncelTutarUSD - toplamOrtMaliyetUSD;

    if (loading && hisseler.length === 0) {
        return (
            <div className="borsa-wrap" style={{ padding: 24, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="borsa-wrap">
            <div className="borsa-actions hide-on-print">
                <Button
                    type="primary"
                    className="btns"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    title="Yeni Ekle"
                    style={{ marginRight: 8 }}
                />
                <Button
                    type="default"
                    className="btns borsa-btn-gunluk"
                    icon={<FileTextOutlined />}
                    onClick={goGunluk}
                    title="Günlük"
                    style={{ marginRight: 8 }}
                />
                <Button
                    type="default"
                    className="btns"
                    icon={<FilePdfOutlined />}
                    onClick={handlePdfDownload}
                    title="PDF İndir"
                />
            </div>
            <div className="borsa-table-wrap" ref={tableRef}>
                <table className="table borsatable btable">
                    <tbody>
                        <tr>
                            <td colSpan={15}>
                                <h3>BORSA RAPORU</h3>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2} className="BIST100">BIST100</td>
                            <td colSpan={1} className={`BIST100 ${bist100Flash || ''}`} id="BIST100">
                                {bist100}
                            </td>
                            <td colSpan={5} className="text-center">Türk Lirası</td>
                            <td colSpan={5} className="text-center">Amerikan Doları</td>
                            <td colSpan={2} className="text-center">Kurlar</td>
                        </tr>
                        <tr>
                            <td>Hisse</td>
                            <td>Adet</td>
                            <td>Aracı Kurum</td>
                            <td>Ort. Maliyet</td>
                            <td>Toplam Maliyet</td>
                            <td>Güncel Fiyat</td>
                            <td>Güncel Tutar</td>
                            <td>Kar/Zarar</td>
                            <td>Ort. Maliyet</td>
                            <td>Toplam Maliyet</td>
                            <td>Güncel Fiyat</td>
                            <td>Güncel Tutar</td>
                            <td>Kar/Zarar</td>
                            <td>Alış USD</td>
                            <td>Güncel USD</td>
                        </tr>
                        {hisseler.map((hisse, idx) => {
                            const isTotal = hisse.kurum === 'TOPLAM';
                            const adet = Number(hisse.adet) || 0;
                            const ortMaliyet = Number(hisse.ort_maliyet) || 0;
                            const guncelFiyat = Number(hisse.guncel_fiyat) || 0;
                            const dolar = Number(hisse.dolar) || 1;
                            const guncelDolar = Number(hisse.guncel_dolar) || 1;
                            const toplamMaliyetTRY = ortMaliyet * adet;
                            const guncelTutarTRY = guncelFiyat * adet;
                            const karZararTRY = guncelTutarTRY - toplamMaliyetTRY;
                            const ortMaliyetUSD = ortMaliyet / dolar;
                            const toplamMaliyetUSD = ortMaliyetUSD * adet;
                            const guncelFiyatUSD = guncelFiyat / guncelDolar;
                            const guncelTutarUSD = guncelFiyatUSD * adet;
                            const karZararUSD = guncelTutarUSD - toplamMaliyetUSD;
                            const rowClass = [rowColors[hisse.kod], isTotal ? 'total' : 'satir'].filter(Boolean).join(' ');
                            return (
                                <tr key={`${hisse.kod}-${hisse.id}-${idx}`} className={rowClass || undefined}>
                                    <td
                                        className={rowColors[hisse.kod] || ''}
                                        style={{ cursor: isTotal ? 'default' : 'pointer', textAlign: 'left' }}
                                        onClick={() => !isTotal && hisse.id && handleEdit(hisse.id)}
                                    >
                                        {hisse.kod}
                                    </td>
                                    <td>{hisse.adet}</td>
                                    <td>{hisse.kurum}</td>
                                    <td>{formatTRY(ortMaliyet)}</td>
                                    <td>{formatTRY(toplamMaliyetTRY)}</td>
                                    <td>{formatTRY(guncelFiyat)}</td>
                                    <td>{formatTRY(guncelTutarTRY)}</td>
                                    <td>{formatTRY(karZararTRY)}</td>
                                    <td>{formatUSD(ortMaliyetUSD)}</td>
                                    <td>{formatUSD(toplamMaliyetUSD)}</td>
                                    <td>{formatUSD(guncelFiyatUSD)}</td>
                                    <td>{formatUSD(guncelTutarUSD)}</td>
                                    <td>{formatUSD(karZararUSD)}</td>
                                    <td>{formatTRY(dolar)}</td>
                                    <td>{formatTRY(guncelDolar)}</td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td colSpan={3}>Toplam</td>
                            <td />
                            <td>{formatTRY(toplamOrtMaliyetTRY)}</td>
                            <td />
                            <td>{formatTRY(toplamGuncelTutarTRY)}</td>
                            <td>{formatTRY(toplamKarZararTRY)}</td>
                            <td />
                            <td>{formatUSD(toplamOrtMaliyetUSD)}</td>
                            <td />
                            <td>{formatUSD(toplamGuncelTutarUSD)}</td>
                            <td>{formatUSD(toplamKarZararUSD)}</td>
                            <td />
                            <td />
                        </tr>
                    </tbody>
                </table>
            </div>

            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnClose={true}
                    width={800}
                    className={darkMode ? 'dark-modal borsa-modal' : 'borsa-modal'}
                    style={darkMode ? { backgroundColor: 'var(--dark-bg-secondary)' } : {}}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            extraData={currentFormExtraData}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}

            {hisseler.length > 0 && (
                <div className="tradingview-widget-container hide-on-print" style={{ height: 500, width: '100%', marginTop: 16 }}>
                    <div id="tradingview_borsa" style={{ height: 500, width: '100%' }} />
                </div>
            )}
        </div>
    );
};

export default Borsa;